import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { notifyNewOrder, notifyOrderStatusChange } from './notificationService';

// ==================== ORDERS ====================
export const addOrder = async (orderData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.ORDERS), {
      ...orderData,
      createdAt: Timestamp.now(),
      status: 'pending'
    });

    await notifyNewOrder({ ...orderData, id: docRef.id });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding order:', error);
    return { success: false, error: error.message };
  }
};

export const getOrders = async () => {
  try {
    const querySnapshot = await getDocs(
      query(collection(db, COLLECTIONS.ORDERS), orderBy('createdAt', 'desc'))
    );
    const orders = [];
    querySnapshot.forEach((doc) => {
      orders.push({ id: doc.id, ...doc.data() });
    });
    return orders;
  } catch (error) {
    console.error('Error getting orders:', error);
    return [];
  }
};

export const updateOrder = async (orderId, updateData) => {
  try {
    const orderRef = doc(db, COLLECTIONS.ORDERS, orderId);
    await updateDoc(orderRef, {
      ...updateData,
      updatedAt: Timestamp.now()
    });

    if (updateData.status) {
      try {
        const orderDoc = await getDoc(orderRef);
        if (orderDoc.exists()) {
          const orderData = orderDoc.data();
          await notifyOrderStatusChange(orderId, orderData.customerName, updateData.status);
        }
      } catch (e) {
        console.error('Error sending notification:', e);
      }
    }

    return { success: true };
  } catch (error) {
    if (error.code === 'not-found') {
      return { success: false, error: 'not-found' };
    }
    console.error('Error updating order:', error);
    return { success: false, error: error.message };
  }
};

// ==================== PRODUCTS ====================
export const addProduct = async (collectionName, productData) => {
  try {
    const docRef = await addDoc(collection(db, collectionName), {
      ...productData,
      createdAt: Timestamp.now()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding product:', error);
    return { success: false, error: error.message };
  }
};

export const getProducts = async (collectionName) => {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    const products = [];
    querySnapshot.forEach((doc) => {
      products.push({ id: doc.id, ...doc.data() });
    });
    return products;
  } catch (error) {
    console.error('Error getting products:', error);
    return [];
  }
};

export const updateProduct = async (collectionName, productId, updateData) => {
  try {
    const productRef = doc(db, collectionName, productId);
    await updateDoc(productRef, {
      ...updateData,
      updatedAt: Timestamp.now()
    });
    return { success: true };
  } catch (error) {
    if (error.code === 'not-found') {
      // إذا لم يكن المنتج موجوداً، قم بإضافته كجديد
      console.log('Product not found, adding as new...');
      return addProduct(collectionName, updateData);
    }
    console.error('Error updating product:', error);
    return { success: false, error: error.message };
  }
};

export const deleteProduct = async (collectionName, productId) => {
  try {
    await deleteDoc(doc(db, collectionName, productId));
    return { success: true };
  } catch (error) {
    if (error.code === 'not-found') {
      return { success: true }; // نعتبره نجاحاً لأنه غير موجود بالفعل
    }
    console.error('Error deleting product:', error);
    return { success: false, error: error.message };
  }
};

// ==================== CUSTOMERS ====================
export const getCustomers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, COLLECTIONS.CUSTOMERS));
    const customers = [];
    querySnapshot.forEach((doc) => {
      customers.push({ id: doc.id, ...doc.data() });
    });
    return customers;
  } catch (error) {
    console.error('Error getting customers:', error);
    return [];
  }
};

// ==================== ANALYTICS ====================
export const getAnalytics = async () => {
  try {
    const orders = await getOrders();
    const accessories = await getProducts(COLLECTIONS.PRODUCTS);
    const parts = await getProducts(COLLECTIONS.PRODUCTS);
    const services = await getProducts(COLLECTIONS.SERVICES);

    // حساب الإحصائيات
    const totalOrders = orders.length;
    const completedOrders = orders.filter(o => o.status === 'completed').length;
    const pendingOrders = orders.filter(o => o.status === 'pending').length;
    const totalRevenue = orders
      .filter(o => o.status === 'completed')
      .reduce((sum, order) => sum + (order.total || 0), 0);

    const totalProducts = accessories.length + parts.length + services.length;

    return {
      totalOrders,
      completedOrders,
      pendingOrders,
      totalRevenue,
      totalProducts,
      recentOrders: orders.slice(0, 5)
    };
  } catch (error) {
    console.error('Error getting analytics:', error);
    return null;
  }
};
